import { useState, useRef, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Dices } from "lucide-react";
import axios from "axios";
import { ensureInningScores, getBatterIndex, getBatterIndexKey, getLineup, showScoringToast, nextBatterIndex, rollHrDistance, isGameOver } from "./utils";
import { HR_PHRASES, TEAM_COLORS, DEFAULT_TEAM_COLOR } from "./constants";
import ManagerCardHand from "./ManagerCardHand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Resolution animation stage delays (ms). Error Card uses a longer multi-stage
// sequence; normal flow is unchanged. Defined at module level — no render deps.
const FIELDING_NORMAL_DELAYS = { 1: 600, 2: 1200, 3: 1000, 4: 3000 };
const FIELDING_ERROR_DELAYS  = { 1: 600, 2: 800,  3: 700, 4: 400, 5: 900, 6: 600, 7: 3000 };

function fieldingOutcomeLabel(outcome, currentOuts = 0) {
  if (outcome === "hr") return { type: "hr", label: "Home Run!", color: "bg-gradient-to-r from-amber-500 to-orange-600", desc: "Over the fence!", bases: 4 };
  if (outcome === "triple") return { type: "triple", label: "Triple", color: "bg-emerald-600", desc: "To third!", bases: 3 };
  if (outcome === "double") return { type: "double", label: "Double", color: "bg-yellow-500 text-black", desc: "To second!", bases: 2 };
  if (outcome === "single") return { type: "single", label: "Single", color: "bg-amber-500", desc: "To first!", bases: 1 };
  if (outcome === "double_play") {
    if (currentOuts >= 2) return { type: "double_play", label: "Out", color: "bg-red-500", desc: "Defense makes the play", bases: 0 };
    return { type: "double_play", label: "Double Play!", color: "bg-red-600", desc: "Two outs!", bases: 0 };
  }
  if (outcome === "triple_play") return { type: "triple_play", label: "Triple Play!", color: "bg-gradient-to-r from-red-700 to-rose-600", desc: "Half-inning over!", bases: 0 };
  return { type: "out", label: "Out", color: "bg-red-500", desc: "Defense makes the play", bases: 0 };
}

const POWER_LABELS = {
  1: { label: "Infield Dribbler", color: "text-slate-400" },
  2: { label: "Weak Contact", color: "text-slate-400" },
  3: { label: "Solid Contact!", color: "text-amber-400" },
  4: { label: "Solid Contact!", color: "text-amber-400" },
  5: { label: "Smashed!", color: "text-orange-400" },
  6: { label: "Roped!", color: "text-orange-400" },
};

function getPowerLabel(contact) {
  if (contact <= 0) return { label: "Dribbler", color: "text-slate-500" };
  if (contact >= 7) return { label: "CRUSHED!", color: "text-red-400" };
  return POWER_LABELS[contact] || { label: "Weak Contact", color: "text-slate-400" };
}

function HomeRunHandler({ game, gameId, setCurrentPhase, onUpdate, onRecordPlay, onLogPlay, isOnline = false, pitchResolutionId = null, preBases = null, preBatter = null, contact = null }) {
  const [processing, setProcessing] = useState(false);
  const appliedRef = useRef(false);
  const state = game.state;
  const battingTeam = state.half === "top" ? game.away_team : game.home_team;
  const fieldingTeam = state.half === "top" ? game.home_team : game.away_team;
  const batters = getLineup(battingTeam);
  const pitchers = fieldingTeam.players.filter(p => p.position === "P");
  const currentPitcher = pitchers.find(p => !p.subbed_out) || pitchers[0];
  const batterIndex = getBatterIndex(state) % (batters.length || 1);
  // After option A the server clears state.bases inline with _apply_hr_outcome before this
  // component mounts. Same with state.X_batter_index — it's already advanced. Use the
  // server-supplied snapshots when available; fall back to live state for offline / local-PvP.
  const basesForCount = preBases || state.bases;
  // Prefer the pre-resolution batter snapshot. Without this, getBatterIndex(state) returns
  // the on-deck batter (because the server has already incremented the index), so the HR
  // toast would credit the WRONG player.
  const fallbackBatter = batters[batterIndex];
  const currentBatter = preBatter || fallbackBatter;
  const runnersOnBase = [basesForCount.third, basesForCount.second, basesForCount.first].filter(Boolean);
  const totalRuns = runnersOnBase.length + 1;
  const scoringNames = [...runnersOnBase.map(r => r.name || 'Runner'), currentBatter?.name || 'Batter'];

  const processHomeRun = async () => {
    if (appliedRef.current) return;
    appliedRef.current = true;
    setProcessing(true);

    // Online: server already applied the HR (contact >= 7 short-circuits in _resolve_pitch
    // and calls _apply_hr_outcome) and broadcast game_state + play_recorded. Client just
    // shows the celebration message and navigates back to pitch.
    if (isOnline) {
      const phrases = HR_PHRASES[totalRuns] || HR_PHRASES[1];
      let message = phrases[Math.floor(Math.random() * phrases.length)];
      scoringNames.forEach((name, idx) => { message = message.replace(`{${idx + 1}}`, name); });
      toast.success(message);
      setCurrentPhase("pitch");
      setProcessing(false);
      return;
    }

    // Record HR stats locally (offline / local PvP only)
    if (onRecordPlay) {
      onRecordPlay(currentPitcher?.id, currentBatter?.id, { type: 'hit', hitType: 'hr', rbi: totalRuns });
    }
    if (onLogPlay) onLogPlay(`HOME RUN! ${currentBatter?.name || 'Batter'} (${totalRuns} RBI)`);

    const newState = { ...state };
    newState.inning_scores = ensureInningScores(state.inning_scores, Math.max(9, state.inning));
    if (state.half === "bottom") newState.home_score = (newState.home_score || 0) + totalRuns;
    else newState.away_score = (newState.away_score || 0) + totalRuns;
    const innIdx = state.inning - 1;
    newState.inning_scores[innIdx][state.half] = (newState.inning_scores[innIdx][state.half] || 0) + totalRuns;
    newState.bases = { first: null, second: null, third: null };
    newState.balls = 0; newState.strikes = 0;
    if (batters.length > 0) newState[getBatterIndexKey(state.half)] = nextBatterIndex(state, batters.length);
    const phrases = HR_PHRASES[totalRuns] || HR_PHRASES[1];
    let message = phrases[Math.floor(Math.random() * phrases.length)];
    scoringNames.forEach((name, idx) => { message = message.replace(`{${idx + 1}}`, name); });
    toast.success(message);
    onUpdate(newState, pitchResolutionId);
    setCurrentPhase("pitch");
    setProcessing(false);
  };

  // Online: auto-apply after a short celebration, then loop back to pitch. No manual button.
  useEffect(() => {
    if (!isOnline) return;
    const t = setTimeout(() => { processHomeRun(); }, 2200);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  return (
    <motion.div initial={{ scale: 0.95 }} animate={{ scale: 1 }} className="h-full flex flex-col items-center justify-center gap-3">
      <motion.div
        initial={{ y: -10, opacity: 0, scale: 0.9 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        className="bg-gradient-to-r from-amber-500 to-orange-600 rounded-lg py-1.5 px-4 text-white text-center whitespace-nowrap"
      >
        <span className="text-sm font-black">💥 Home Run!</span>
        {/* Distance — same seed as the cinematic ball counter so the number
            on the banner exactly matches what's ticking up overhead. Power =
            the auto-HR contact value (≥7); seed = pitch resolution_id. */}
        {contact != null && pitchResolutionId && (
          <span className="ml-2 text-xs font-bold opacity-95">
            {rollHrDistance(contact, pitchResolutionId)} ft
          </span>
        )}
      </motion.div>
      <p className="text-sm text-slate-300">{totalRuns === 1 ? "Solo shot!" : `${totalRuns} runs score!`}</p>
      {!isOnline && (
        <div className="flex items-center gap-2">
          <button onClick={() => setCurrentPhase("pitch")}
            className="px-3 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs transition-all">
            Re-roll
          </button>
          <button onClick={processHomeRun} disabled={processing}
            className="px-4 py-1 rounded-md bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs transition-all">
            {processing ? "..." : `Score ${totalRuns}`}
          </button>
        </div>
      )}
    </motion.div>
  );
}

export default function FieldingPhase({
  phaseSync = null,
  onPhaseSyncApplied = null,
  keyboardBlocked = false,
  onFieldingPhaseChange = null,
  game, gameId, pitchResult, setCurrentPhase, setActivePositions, onUpdate: rawOnUpdate, onRecordPlay, onLogPlay,
  activeCards = [], onHalfInningEnd,
  // Online props
  isOnline = false, myRole = null, wsSend = null, wsConnected = true,
  onlineFieldingLocked = null, onlineFieldingResolution = null, onClearFieldingResolution = null,
  onResultRevealed = null,
  homeHand = [], awayHand = [], allCards = [], homeSpentCards = [], awaySpentCards = [],
  onCardsPlayed = null, onInstantCardPlayed = null, onHackTheBoardPlay = null,
  onPitchClockUpdate = null, pitchClockSync = null,
  gameOver = false,
  onHrShow = null,
  handRefreshActive = false,
  // Phase 5/6: each side's persistent active cards (server-side). Distinct from
  // the above `activeCards` prop (which is the per-pitch played-cards array — a
  // legacy prop name). Wired into ManagerCardHand below.
  homeActiveCards = [], awayActiveCards = [],
  onDeactivateCard = null,
  onToughHopModeChange = null,
  onDirectionalHittingModeChange = null,
  activeListeners = {},
  onToggleListener = null,
}) {
  // Wrap onUpdate to inject the current resolution_id so concurrent PUTs from both clients
  // are idempotent server-side.
  const onUpdate = useCallback((newState) => {
    const rid = onlineFieldingResolution?.resolution_id || null;
    rawOnUpdate(newState, rid);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawOnUpdate, onlineFieldingResolution]);

  const [fieldRoll, setFieldRoll] = useState("");
  const [managerMod, setManagerMod] = useState("");
  const [contactValue, setContactValue] = useState(pitchResult?.contact || 0);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (pitchResult?.contact != null) setContactValue(pitchResult.contact);
  }, [pitchResult]);
  const [processing, setProcessing] = useState(false);
  const [isRolling, setIsRolling] = useState(false);
  const rollInterval = useRef(null);

  const state = game.state;
  const isAutoHR = contactValue >= 7;
  const battingTeam = state.half === "top" ? game.away_team : game.home_team;
  const fieldingTeam = state.half === "top" ? game.home_team : game.away_team;
  const batters = getLineup(battingTeam);
  const pitchers = fieldingTeam.players.filter(p => p.position === "P");
  const currentPitcher = pitchers.find(p => !p.subbed_out) || pitchers[0];
  const batterIndex = getBatterIndex(state) % (batters.length || 1);
  const currentBatter = batters[batterIndex];
  const hasForce = !!state.bases.first;
  const fieldingCardBonus = (activeCards || []).reduce((sum, c) => sum + (c.fielding_modifier || 0), 0);
  const fieldTotal = (parseInt(fieldRoll) || 0) + (parseInt(managerMod) || 0) + fieldingCardBonus;
  const power = getPowerLabel(contactValue);

  // Online state
  const fieldingTeamKey = state.half === "top" ? "home" : "away";
  const battingTeamKey = fieldingTeamKey === "home" ? "away" : "home";
  const amIFielder = isOnline && myRole === fieldingTeamKey;
  const amIBatter = isOnline && myRole === battingTeamKey;
  const myHand = amIFielder ? (fieldingTeamKey === "home" ? homeHand : awayHand)
    : amIBatter ? (battingTeamKey === "home" ? homeHand : awayHand)
    : [];
  const mySpent = amIFielder ? (fieldingTeamKey === "home" ? homeSpentCards : awaySpentCards)
    : amIBatter ? (battingTeamKey === "home" ? homeSpentCards : awaySpentCards)
    : [];
  const myActive = amIFielder ? (fieldingTeamKey === "home" ? homeActiveCards : awayActiveCards)
    : amIBatter ? (battingTeamKey === "home" ? homeActiveCards : awayActiveCards)
    : [];

  // Rookie Jitters condition: any non-subbed fielding team player with exactly 1 season.
  const _fieldingTeamHasRookie = (fieldingTeam?.players || [])
    .filter(p => !p.subbed_out)
    .some(p => (p.seasons || []).length === 1);

  const debugMode = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("debug") === "1";

  // Online phase: select | waiting | resolution
  const [onlinePhase, setOnlinePhase] = useState("select");

  // Report the fielding sub-phase up so GameBoard's expectedAction can tell
  // "you owe a roll" from "you already rolled and are waiting". Without this the
  // state blob demanded fielding_lock_in while this component correctly rendered
  // "YOU'RE LOCKED IN" and no control — an audit reasonably called that
  // unwinnable by construction.
  useEffect(() => {
    onFieldingPhaseChange?.(onlinePhase);
  }, [onlinePhase, onFieldingPhaseChange]);

  // ── Keyboard: Enter locks the fielding roll ────────────────────────────────
  // The active branch publishes its action here; one window listener reads it.
  // Cleared every render so a branch that doesn't publish leaves nothing wired.
  const kbdFieldRef = useRef(null);
  kbdFieldRef.current = null;
  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
      if (keyboardBlocked) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable
                || t.tagName === "SELECT")) return;
      if ((e.key || "").toLowerCase() !== "enter") return;
      const act = kbdFieldRef.current;
      if (!act) return;
      e.preventDefault();
      act();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [keyboardBlocked]);

  const _appliedPhaseSyncRef = useRef(undefined);
  // Server-authoritative fielding lock-in restore. Same rationale as PitchingPhase:
  // a reload used to come back with a live Lock In button even though the server
  // already had this role's submission, which is how a fielding phase could sit
  // waiting on a player who had in fact already acted.
  useEffect(() => {
    if (!isOnline) return;
    // Adopt-on-mount then latch — see PitchingPhase for the full rationale. This
    // component remounts on EVERY ball in play, so without the adopt step the last
    // phase_sync would be re-applied on each new fielding phase and skip the player
    // straight to "You're locked in" having submitted nothing.
    if (_appliedPhaseSyncRef.current === undefined) {
      _appliedPhaseSyncRef.current = phaseSync;
      return;
    }
    if (!phaseSync) return;
    if (_appliedPhaseSyncRef.current === phaseSync) return;
    _appliedPhaseSyncRef.current = phaseSync;
    // in_play gates it further: the server only reports a live batted ball while one
    // is actually pending, so a snapshot from a finished play can't restore a lock.
    if (phaseSync.fielding_locked && phaseSync.in_play && onlinePhase === "select") {
      setOnlinePhase("waiting");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phaseSync, isOnline]);
  const [myCards, setMyCards] = useState([]);

  // Tough Hop: batting team selects a target infielder from the opponent roster.
  const [toughHopTarget, setToughHopTarget] = useState(null);
  const INFIELD_POSITIONS = ["1B", "2B", "3B", "SS"];
  // Eligible regardless of contact (used for canPlayCard gating via augmentedGameState).
  const toughHopEligibleAll = (fieldingTeam?.players || []).filter(
    p => !p.subbed_out
      && INFIELD_POSITIONS.includes((p.position || "").toUpperCase())
      && !(p.seasons || []).some(s => (s?.stats?.stolen_bases || 0) > 15)
  );
  const toughHopSelected = amIBatter && myCards.includes("tough_hop") && contactValue >= 1 && contactValue <= 4;
  const toughHopEligible = toughHopSelected ? toughHopEligibleAll : [];
  const effectiveToughHopTarget = toughHopSelected
    ? (toughHopEligible.length === 1 ? toughHopEligible[0] : toughHopTarget)
    : null;
  const toughHopNeedsTarget = toughHopSelected && !effectiveToughHopTarget && toughHopEligible.length > 1;

  // Push tough hop target-select mode up to GameBoard so DiamondField can highlight tiles.
  // Active only when: card selected, contact 1–4, multiple eligible, no target chosen yet.
  const handleToughHopSelect = (player) => {
    setToughHopTarget(prev => prev?.id === player.id ? null : player);
  };

  useEffect(() => {
    if (!onToughHopModeChange) return;
    if (toughHopSelected && toughHopEligible.length > 1) {
      onToughHopModeChange({
        eligibleIds: toughHopEligible.map(p => String(p.id)),
        selectedId: effectiveToughHopTarget ? String(effectiveToughHopTarget.id) : null,
        colorHex: (TEAM_COLORS[fieldingTeam?.color] || DEFAULT_TEAM_COLOR).hex,
        onSelect: handleToughHopSelect,
      });
    } else {
      onToughHopModeChange(null);
    }
    return () => { onToughHopModeChange?.(null); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toughHopSelected, toughHopEligible.length, effectiveToughHopTarget, fieldingTeam?.color]);

  // Directional Hitting & Delayed Reaction: batting team selects an opponent fielder (not P/C)
  // WITHOUT 100+ G in majority of seasons — targets part-timers / bench players.
  // Both cards share the same eligibility pool and diamond-highlight targeting UI.
  const [fielderCardTarget, setFielderCardTarget] = useState(null);
  const lacksHighGamesMajority = (player) => {
    const seasons = player?.seasons || [];
    if (!seasons.length) return true;
    const highGameCount = seasons.filter(s => (s?.stats?.games_played || 0) >= 100).length;
    return highGameCount <= seasons.length / 2;
  };
  const fielderCardEligibleAll = (fieldingTeam?.players || []).filter(
    p => !p.subbed_out
      && !["P", "C"].includes((p.position || "").toUpperCase())
      && lacksHighGamesMajority(p)
  );
  const directionalHittingSelected = amIBatter && myCards.includes("directional_hitting");
  const delayedReactionSelected    = amIBatter && myCards.includes("delayed_reaction");
  const fielderCardSelected = directionalHittingSelected || delayedReactionSelected;
  const fielderCardEligible = fielderCardSelected ? fielderCardEligibleAll : [];
  const effectiveFielderCardTarget = fielderCardSelected
    ? (fielderCardEligible.length === 1 ? fielderCardEligible[0] : fielderCardTarget)
    : null;
  const fielderCardNeedsTarget = fielderCardSelected && !effectiveFielderCardTarget && fielderCardEligible.length > 1;
  // Aliases kept for lock-in code below
  const effectiveDirectionalHittingTarget = directionalHittingSelected ? effectiveFielderCardTarget : null;
  const effectiveDelayedReactionTarget    = delayedReactionSelected    ? effectiveFielderCardTarget : null;

  // Toggle select: clicking the selected player deselects them.
  const handleFielderCardSelect = (player) => {
    setFielderCardTarget(prev => prev?.id === player.id ? null : player);
  };

  // Push fielder target-select mode up to GameBoard so DiamondField can highlight tiles.
  useEffect(() => {
    if (!onDirectionalHittingModeChange) return;
    if (fielderCardSelected && fielderCardEligible.length > 1) {
      onDirectionalHittingModeChange({
        eligibleIds: fielderCardEligible.map(p => String(p.id)),
        selectedId: effectiveFielderCardTarget ? String(effectiveFielderCardTarget.id) : null,
        colorHex: (TEAM_COLORS[fieldingTeam?.color] || DEFAULT_TEAM_COLOR).hex,
        onSelect: handleFielderCardSelect,
      });
    } else {
      onDirectionalHittingModeChange(null);
    }
    return () => { onDirectionalHittingModeChange?.(null); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fielderCardSelected, fielderCardEligible.length, effectiveFielderCardTarget, fieldingTeam?.color]);

  const augmentedGameState = {
    ...state,
    total_innings: game?.innings_total ?? 9,
    _fieldingTeamHasRookie,
    _fielding_contact: contactValue,
    _toughHopHasEligible: toughHopEligibleAll.length > 0,
    _directionalHittingHasEligible: fielderCardEligibleAll.length > 0,
  };
  const [actionTimer, setActionTimer] = useState(null);
  const actionTimerRef = useRef(null);
  // Animated resolution stages.
  // Normal:      1 cards → 2 spin → 3 land → 4 result → 5 done
  // Error Card:  1 cards → 2 first spin → 3 first land → 4 reroll banner → 5 second spin → 6 second land → 7 result → 8 done
  const [resolutionStage, setResolutionStage] = useState(0);
  const [diceDisplay, setDiceDisplay] = useState(0);
  const [diceDisplay2, setDiceDisplay2] = useState(0);
  const [fieldingDieAnimated, setFieldingDieAnimated] = useState(null);
  const diceSpinRef = useRef(null);
  const [fieldPanelWidth, setFieldPanelWidth] = useState(220);
  const [fieldPanelDragging, setFieldPanelDragging] = useState(false);
  const fieldPanelContainerRef = useRef(null);

  useEffect(() => {
    if (!fieldPanelDragging) return;
    const handleMove = (e) => {
      if (!fieldPanelContainerRef.current) return;
      const rect = fieldPanelContainerRef.current.getBoundingClientRect();
      const newW = e.clientX - rect.left;
      setFieldPanelWidth(Math.min(Math.max(newW, 140), 420));
    };
    const handleUp = () => setFieldPanelDragging(false);
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleUp);
    };
  }, [fieldPanelDragging]);

  const rollD6 = () => {
    // If the user has already manually entered a value, use it — don't overwrite
    if (fieldRoll !== "") {
      calculate();
      return;
    }

    setIsRolling(true);
    setResult(null);

    // Animate rolling numbers
    rollInterval.current = setInterval(() => {
      setFieldRoll(String(Math.floor(Math.random() * 6) + 1));
    }, 60);

    setTimeout(() => {
      clearInterval(rollInterval.current);
      const roll = Math.floor(Math.random() * 6) + 1;
      setFieldRoll(String(roll));
      setIsRolling(false);

      // Auto-calculate result
      const fTotal = roll + (parseInt(managerMod) || 0) + fieldingCardBonus;
      const diff = contactValue - fTotal;
      let outcome;
      if (diff >= 7) outcome = { type: "hr", label: "Home Run!", color: "bg-gradient-to-r from-amber-500 to-orange-600", desc: "Over the fence!", bases: 4 };
      else if (diff >= 5) outcome = { type: "triple", label: "Triple", color: "bg-emerald-600", desc: "To third!", bases: 3 };
      else if (diff >= 3) outcome = { type: "double", label: "Double", color: "bg-yellow-500 text-black", desc: "To second!", bases: 2 };
      else if (diff >= 1) outcome = { type: "single", label: "Single", color: "bg-amber-500", desc: "To first!", bases: 1 };
      else if (diff === 0) outcome = { type: "single", label: "Single", color: "bg-amber-500", desc: "Close play - safe!", bases: 1 };
      else if (diff <= -2 && hasForce) outcome = state.outs >= 2
        ? { type: "double_play", label: "Out", color: "bg-red-500", desc: "Defense makes the play", bases: 0 }
        : { type: "double_play", label: "Double Play!", color: "bg-red-600", desc: "Two outs!", bases: 0 };
      else outcome = { type: "out", label: "Out", color: "bg-red-500", desc: "Defense makes the play", bases: 0 };
      setResult({ fieldRoll: roll, managerMod: parseInt(managerMod) || 0, fieldTotal: fTotal, contact: contactValue, diff, ...outcome });
    }, 500);
  };

  const calculate = () => {
    if (!fieldRoll) return;
    const diff = contactValue - fieldTotal;
    let outcome;
    if (diff >= 7) outcome = { type: "hr", label: "Home Run!", color: "bg-gradient-to-r from-amber-500 to-orange-600", desc: "Over the fence!", bases: 4 };
    else if (diff >= 5) outcome = { type: "triple", label: "Triple", color: "bg-emerald-600", desc: "To third!", bases: 3 };
    else if (diff >= 3) outcome = { type: "double", label: "Double", color: "bg-yellow-500 text-black", desc: "To second!", bases: 2 };
    else if (diff >= 1) outcome = { type: "single", label: "Single", color: "bg-amber-500", desc: "To first!", bases: 1 };
    else if (diff === 0) outcome = { type: "single", label: "Single", color: "bg-amber-500", desc: "Close play - safe!", bases: 1 };
    else if (diff <= -2 && hasForce) outcome = state.outs >= 2
      ? { type: "double_play", label: "Out", color: "bg-red-500", desc: "Defense makes the play", bases: 0 }
      : { type: "double_play", label: "Double Play!", color: "bg-red-600", desc: "Two outs!", bases: 0 };
    else outcome = { type: "out", label: "Out", color: "bg-red-500", desc: "Defense makes the play", bases: 0 };
    setResult({ fieldRoll: parseInt(fieldRoll), managerMod: parseInt(managerMod) || 0, fieldTotal, contact: contactValue, diff, ...outcome });
  };

  // Online: request 30s server-synced clock when entering select. Idempotent — server returns
  // existing if active. Same watchdog pattern as PitchingPhase: a dropped request would
  // strand the at-bat with no clock, so retry every 5s until pitchClockSync lands.
  useEffect(() => {
    if (!isOnline || debugMode || !wsSend || !wsConnected) return;
    if (gameOver) return;
    if (handRefreshActive) return;
    if (onlinePhase !== "select") return;
    // Auto-HR (contact >= 7): HomeRunHandler shows for ~2.2s then calls setCurrentPhase("pitch").
    // No fielding roll happens, so don't request a fielding clock — it would cause the server to
    // schedule _auto_complete_fielding which fires 32s later and broadcasts phase_reset because
    // there's no in_play_pitches entry, breaking the game state machine.
    if (isAutoHR) return;
    const haveActiveFieldingClock =
      pitchClockSync &&
      pitchClockSync.kind === "fielding" &&
      pitchClockSync.started_at + pitchClockSync.duration * 1000 > Date.now();
    if (haveActiveFieldingClock) return;
    wsSend({ type: "request_clock", kind: "fielding" });
    const interval = setInterval(() => {
      wsSend({ type: "request_clock", kind: "fielding" });
    }, 5000);
    return () => clearInterval(interval);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, debugMode, onlinePhase, pitchClockSync, wsConnected, gameOver, handRefreshActive, isAutoHR]);

  // Server-synced fielding clock ticker. Auto-locks me with current cards if I haven't locked in.
  const autoActionedRef = useRef(false);
  useEffect(() => {
    if (!isOnline || debugMode || gameOver) {
      clearInterval(actionTimerRef.current);
      setActionTimer(null);
      return;
    }
    const isClockPhase = onlinePhase === "select" || onlinePhase === "waiting";
    // isAutoHR: no fielding roll for auto-HR, don't auto-lock-in
    if (isAutoHR || !isClockPhase || !pitchClockSync || pitchClockSync.kind !== "fielding" || handRefreshActive) {
      clearInterval(actionTimerRef.current);
      setActionTimer(null);
      autoActionedRef.current = false;
      return;
    }
    const computeRemaining = () => {
      const expiresAt = pitchClockSync.started_at + pitchClockSync.duration * 1000;
      return Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
    };
    const tick = () => {
      const remaining = computeRemaining();
      setActionTimer(remaining);
      if (remaining === 0 && !autoActionedRef.current && onlinePhase === "select" && wsSend) {
        autoActionedRef.current = true;
        wsSend({ type: "fielding_lock_in", cards: myCards });
        setOnlinePhase("waiting");
      }
    };
    tick();
    actionTimerRef.current = setInterval(tick, 250);
    return () => clearInterval(actionTimerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, debugMode, onlinePhase, pitchClockSync, myCards, gameOver, handRefreshActive]);

  // Reset auto-action latch on a fresh clock
  useEffect(() => { autoActionedRef.current = false; }, [pitchClockSync?.started_at]);

  // Publish ticker value to GameBoard's PitchClock widget while we're in a fielding clock phase.
  // Suppressed when no_timers is on (server uses a 10-min AFK safety net but the UI shouldn't surface it).
  useEffect(() => {
    if (!onPitchClockUpdate) return;
    const isClockPhase = isOnline && (onlinePhase === "select" || onlinePhase === "waiting");
    onPitchClockUpdate(isClockPhase && !game?.no_timers ? actionTimer : null);
  }, [actionTimer, onlinePhase, isOnline, onPitchClockUpdate, game?.no_timers]);

  // Clear published clock on unmount
  useEffect(() => () => { if (onPitchClockUpdate) onPitchClockUpdate(null); }, [onPitchClockUpdate]);

  // Online: when fielding_resolution arrives, transition to resolution + start animation.
  // Guard only against re-entering an already-running resolution.
  useEffect(() => {
    if (!isOnline || debugMode || !onlineFieldingResolution) return;
    if (onlinePhase === "resolution") return;
    setOnlinePhase("resolution");
    setResolutionStage(1);
    setDiceDisplay(1);
    setFieldingDieAnimated(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, debugMode, onlineFieldingResolution, onlinePhase]);

  // Resolution animation stage timer. Error Card uses a longer multi-stage map;
  // normal flow is unchanged.
  useEffect(() => {
    if (onlinePhase !== "resolution") return;
    const isErrorCard = onlineFieldingResolution?.error_reroll_d6 != null;
    const DELAYS = isErrorCard ? FIELDING_ERROR_DELAYS : FIELDING_NORMAL_DELAYS;
    if (resolutionStage in DELAYS) {
      const t = setTimeout(() => setResolutionStage(s => s + 1), DELAYS[resolutionStage]);
      return () => clearTimeout(t);
    }
  }, [onlinePhase, resolutionStage, onlineFieldingResolution]);

  // Dice spin animation. Error Card path: stage 2 spins die 1, stage 3 lands it on
  // original_field_d6; stage 5 spins die 2, stage 6 lands it on error_reroll_d6.
  // Normal path: stage 2 spins, stage 3 lands on field_d6.
  useEffect(() => {
    if (onlinePhase !== "resolution") return;
    const r = onlineFieldingResolution;
    const isErrorCard = r?.error_reroll_d6 != null;

    if (isErrorCard) {
      if (resolutionStage === 2) {
        diceSpinRef.current = setInterval(() => setDiceDisplay(Math.floor(Math.random() * 6) + 1), 60);
        return () => clearInterval(diceSpinRef.current);
      }
      if (resolutionStage === 3) {
        clearInterval(diceSpinRef.current);
        setDiceDisplay(r.original_field_d6);
        return;
      }
      if (resolutionStage === 5) {
        diceSpinRef.current = setInterval(() => setDiceDisplay2(Math.floor(Math.random() * 6) + 1), 60);
        return () => clearInterval(diceSpinRef.current);
      }
      if (resolutionStage === 6) {
        clearInterval(diceSpinRef.current);
        setDiceDisplay2(r.error_reroll_d6);
      }
      return;
    }

    // Normal path
    if (resolutionStage === 2) {
      diceSpinRef.current = setInterval(() => setDiceDisplay(Math.floor(Math.random() * 6) + 1), 60);
      return () => clearInterval(diceSpinRef.current);
    }
    if (resolutionStage >= 3 && r) {
      clearInterval(diceSpinRef.current);
      setDiceDisplay(r.field_d6);
    }
  }, [onlinePhase, resolutionStage, onlineFieldingResolution]);

  // Modifier tick: after the fielding die lands (stage 3, normal path only), pause
  // briefly then count from raw field_d6 to field_total. 250ms settle → 130ms/step.
  useEffect(() => {
    if (onlinePhase !== "resolution") return;
    const r = onlineFieldingResolution;
    if (!r || r.error_reroll_d6 != null) return;
    if (resolutionStage !== 3) return;
    if (r.fielding_mod === 0) return;
    let cur = r.field_d6;
    const total = r.field_total;
    const step = total > cur ? 1 : -1;
    let tickId;
    const startId = setTimeout(() => {
      tickId = setInterval(() => {
        cur += step;
        setFieldingDieAnimated(cur);
        if (cur === total) clearInterval(tickId);
      }, 130);
    }, 250);
    return () => { clearTimeout(startId); if (tickId) clearInterval(tickId); };
  }, [onlinePhase, resolutionStage, onlineFieldingResolution]);

  // Fire HR ball cinematic when the result badge appears. Stage 4 normally; stage 7 for Error Card.
  const hrShownForResRef = useRef(null);
  useEffect(() => {
    if (onlinePhase !== "resolution") return;
    const resultStage = onlineFieldingResolution?.error_reroll_d6 != null ? 7 : 4;
    if (resolutionStage < resultStage) return;
    if (!onlineFieldingResolution || onlineFieldingResolution.outcome !== "hr") return;
    if (hrShownForResRef.current === onlineFieldingResolution.resolution_id) return;
    hrShownForResRef.current = onlineFieldingResolution.resolution_id;
    // Power = `diff` (contact - field_total) for fielded HRs. A barely-cleared HR
    // (diff 7) gets the low-end distance bias; a monster diff 12+ flattens toward
    // the top of the band. Seed = resolution_id so the cinematic + badge agree.
    if (onHrShow) onHrShow(onlineFieldingResolution.diff, onlineFieldingResolution.resolution_id);
  }, [onlinePhase, resolutionStage, onlineFieldingResolution, onHrShow]);

  // Play-log sync — fire onResultRevealed when the result badge appears (stage 4
  // normal, stage 7 for the Error Card reroll path) so GameBoard drains the
  // matching held log entry in step with the badge, not after the full
  // animation + advance.
  const resultRevealedFiredRef = useRef(null);
  useEffect(() => {
    if (onlinePhase !== "resolution" || !onlineFieldingResolution) return;
    const resultStage = onlineFieldingResolution.error_reroll_d6 != null ? 7 : 4;
    if (resolutionStage < resultStage) return;
    const rid = onlineFieldingResolution.resolution_id;
    if (!rid || resultRevealedFiredRef.current === rid) return;
    resultRevealedFiredRef.current = rid;
    if (onResultRevealed) onResultRevealed(rid);
  }, [onlinePhase, resolutionStage, onlineFieldingResolution, onResultRevealed]);

  // Resolution complete → only the fielding role's client writes state; batter follows via game_state WS
  useEffect(() => {
    const advanceAt = onlineFieldingResolution?.error_reroll_d6 != null ? 8 : 5;
    if (onlinePhase !== "resolution" || resolutionStage < advanceAt || !onlineFieldingResolution) return;
    const r = onlineFieldingResolution;
    // Build the result object the existing applyResult() consumes
    const labeled = fieldingOutcomeLabel(r.outcome, state.outs);
    setResult({
      ...labeled,
      fieldRoll: r.field_d6,
      managerMod: 0,
      fieldTotal: r.field_total,
      contact: r.contact,
      diff: r.diff,
    });
    // Notify chips/cards spent
    if (onCardsPlayed) {
      const cardById = (id) => [...homeHand, ...awayHand].find(c => c.id === id) || allCards.find(c => c.id === id);
      const fieldingCards = (r.fielding_cards || []).map(id => ({ ...cardById(id), playedBy: "pitcher" })).filter(c => c && c.id);
      const battingCards = (r.batting_cards || []).map(id => ({ ...cardById(id), playedBy: "batter" })).filter(c => c && c.id);
      onCardsPlayed([...fieldingCards, ...battingCards], fieldingTeamKey, battingTeamKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onlinePhase, resolutionStage, onlineFieldingResolution]);

  // After result is set in online mode, both clients call applyResult. PUTs are idempotent
  // server-side (keyed by resolution_id), so concurrent writes are safe and connection failure
  // on one side is covered by the other. Local stats/log update once per client.
  useEffect(() => {
    const advanceAt2 = onlineFieldingResolution?.error_reroll_d6 != null ? 8 : 5;
    if (!isOnline || debugMode || !result || onlinePhase !== "resolution" || resolutionStage < advanceAt2) return;
    // Always clear onlineFieldingResolution even when the game ended — GameBoard gates its
    // victory modal on isAnimating (a resolution prop set), so this clear has to fire so the
    // modal appears after the user has seen the play.
    const t = setTimeout(() => {
      if (onClearFieldingResolution) onClearFieldingResolution();
      applyResult();
    }, 800);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, onlinePhase, resolutionStage, gameOver]);

  if (isAutoHR) return <HomeRunHandler
    game={game}
    gameId={gameId}
    setCurrentPhase={setCurrentPhase}
    onUpdate={rawOnUpdate}
    isOnline={isOnline}
    pitchResolutionId={pitchResult?.resolution_id || null}
    preBases={pitchResult?.pre_bases || null}
    preBatter={pitchResult?.pre_batter || null}
    contact={pitchResult?.contact ?? null}
    onRecordPlay={onRecordPlay}
    onLogPlay={onLogPlay}
  />;

  const applyResult = async () => {
    if (!result) return;
    if (!isOnline && isGameOver(state, game.innings_total || 9)) return;
    setProcessing(true);
    // Online: server applied the fielding outcome inline when it broadcast fielding_resolution
    // and emitted play_recorded for stats/log. Client just transitions back to pitch.
    if (isOnline) {
      setCurrentPhase("pitch");
      clear();
      setProcessing(false);
      return;
    }
    const newState = { ...state };
    let halfInningEnded = false;
    const newBases = { ...state.bases };
    const batterData = { name: currentBatter?.name, id: currentBatter?.id };
    newState.inning_scores = ensureInningScores(state.inning_scores, Math.max(9, state.inning));

    // Calculate runs that will score for RBI tracking
    let runsToScore = 0;
    const basesToAdvance = result.bases || 0;
    
    if (result.type === "hr" || basesToAdvance === 4) {
      // Home run - count all runners + batter
      runsToScore = 1; // batter
      if (state.bases.first) runsToScore++;
      if (state.bases.second) runsToScore++;
      if (state.bases.third) runsToScore++;
    } else if (result.type === "triple" || basesToAdvance === 3) {
      if (state.bases.third) runsToScore++;
      if (state.bases.second) runsToScore++;
      if (state.bases.first) runsToScore++;
    } else if (result.type === "double" || basesToAdvance === 2) {
      if (state.bases.third) runsToScore++;
      if (state.bases.second) runsToScore++;
    } else if (result.type === "single" || basesToAdvance === 1) {
      if (state.bases.third) runsToScore++;
    }
    
    // Record play stats with RBI
    if (onRecordPlay) {
      if (result.type === "out") {
        onRecordPlay(currentPitcher?.id, currentBatter?.id, { type: 'out', outs: 1 });
      } else if (result.type === "double_play") {
        onRecordPlay(currentPitcher?.id, currentBatter?.id, { type: 'out', outs: 2 });
      } else if (result.type === "hr") {
        onRecordPlay(currentPitcher?.id, currentBatter?.id, { type: 'hit', hitType: 'hr', rbi: runsToScore });
      } else if (result.type === "triple") {
        onRecordPlay(currentPitcher?.id, currentBatter?.id, { type: 'hit', hitType: 'triple', rbi: runsToScore });
      } else if (result.type === "double") {
        onRecordPlay(currentPitcher?.id, currentBatter?.id, { type: 'hit', hitType: 'double', rbi: runsToScore });
      } else if (result.type === "single") {
        onRecordPlay(currentPitcher?.id, currentBatter?.id, { type: 'hit', hitType: 'single', rbi: runsToScore });
      }
    }

    // Log the fielding outcome
    if (onLogPlay) {
      const name = currentBatter?.name || 'Batter';
      const rbi = runsToScore > 0 ? ` (${runsToScore} RBI)` : '';
      if (result.type === "out") onLogPlay(`OUT — ${name} retired${rbi}`);
      else if (result.type === "double_play") onLogPlay(`DOUBLE PLAY!${rbi}`);
      else if (result.type === "hr") onLogPlay(`HOME RUN! ${name}${rbi}`);
      else if (result.type === "triple") onLogPlay(`TRIPLE! ${name}${rbi}`);
      else if (result.type === "double") onLogPlay(`DOUBLE! ${name}${rbi}`);
      else if (result.type === "single") onLogPlay(`SINGLE — ${name}${rbi}`);
    }

    if (result.type === "double_play") {
      newState.outs = state.outs + 2;
      // Lead runner + runner directly behind are both out. No runs score on a DP.
      // Any other runners hold their current base.
      if (state.bases.third && state.bases.second && state.bases.first) {
        // Bases loaded: 3rd (lead) OUT, 2nd (behind) OUT, 1st runner → 2nd, batter → 1st
        newBases.third = null;
        newBases.second = { ...state.bases.first };
        newBases.first = { ...batterData };
      } else if (state.bases.second && state.bases.first) {
        // 1st+2nd: 2nd (lead) OUT, 1st (behind) OUT, batter → 1st
        newBases.third = null;
        newBases.second = null;
        newBases.first = { ...batterData };
      } else if (state.bases.third && state.bases.first) {
        // 1st+3rd: 1st (lead) OUT, batter (behind) OUT, 3rd holds
        newBases.third = { ...state.bases.third };
        newBases.second = null;
        newBases.first = null;
      } else {
        // 1st only: 1st runner OUT, batter OUT, bases empty
        newBases.third = null;
        newBases.second = null;
        newBases.first = null;
      }
      // Always announce the DP itself; if it also ended the inning, fire the inning-over
      // toast as a separate beat. Without the unconditional DP toast, an inning-ending
      // double play silently dropped the "Double play!" flavor and only said "Inning over!"
      toast.success("Double play!");
      if (newState.outs >= 3) {
        if (newState.inning_scores[state.inning - 1][state.half] === null) newState.inning_scores[state.inning - 1][state.half] = 0;
        newState.outs = 0; newState.balls = 0; newState.strikes = 0; newState.bases = { first: null, second: null, third: null };
        newState[getBatterIndexKey(state.half)] = nextBatterIndex(state, batters.length);
        newState.half = state.half === "top" ? "bottom" : "top"; if (state.half === "bottom") newState.inning += 1; toast.info("Inning over!");
        halfInningEnded = true;
      } else { newState.bases = newBases; newState[getBatterIndexKey(state.half)] = nextBatterIndex(state, batters.length); }
    } else if (result.type === "out") {
      newState.outs = state.outs + 1;
      if (hasForce) {
        let leadBase = state.bases.third && state.bases.second && state.bases.first ? 'third' : state.bases.second && state.bases.first ? 'second' : 'first';
        const outRunner = state.bases[leadBase];
        if (newState.outs >= 3) {
          if (newState.inning_scores[state.inning - 1][state.half] === null) newState.inning_scores[state.inning - 1][state.half] = 0;
          newState.outs = 0; newState.balls = 0; newState.strikes = 0; newState.bases = { first: null, second: null, third: null };
          newState[getBatterIndexKey(state.half)] = nextBatterIndex(state, batters.length);
          newState.half = state.half === "top" ? "bottom" : "top"; if (state.half === "bottom") newState.inning += 1;
          halfInningEnded = true;
        } else {
          if (leadBase === 'third') { newBases.third = { ...state.bases.second }; newBases.second = { ...state.bases.first }; newBases.first = { ...batterData }; }
          else if (leadBase === 'second') { newBases.second = { ...state.bases.first }; newBases.first = { ...batterData }; }
          else { newBases.first = { ...batterData }; }
          toast.success(`${outRunner?.name || 'Runner'} out! Batter safe.`);
          newState.bases = newBases; newState[getBatterIndexKey(state.half)] = nextBatterIndex(state, batters.length);
        }
      } else {
        if (newState.outs >= 3) {
          if (newState.inning_scores[state.inning - 1][state.half] === null) newState.inning_scores[state.inning - 1][state.half] = 0;
          newState.outs = 0; newState.balls = 0; newState.strikes = 0; newState.bases = { first: null, second: null, third: null };
          newState[getBatterIndexKey(state.half)] = nextBatterIndex(state, batters.length);
          newState.half = state.half === "top" ? "bottom" : "top"; if (state.half === "bottom") newState.inning += 1;
          halfInningEnded = true;
        } else { newState[getBatterIndexKey(state.half)] = nextBatterIndex(state, batters.length); }
      }
    } else {
      const basesToAdvance = result.bases;
      let runsScored = 0; const scoringRunners = [];
      if (state.bases.third) { runsScored++; scoringRunners.push(state.bases.third.name || 'Runner'); newBases.third = null; }
      if (state.bases.second) {
        const newBase = 2 + basesToAdvance;
        if (newBase >= 4) { runsScored++; scoringRunners.push(state.bases.second.name || 'Runner'); newBases.second = null; }
        else if (newBase === 3) { newBases.third = { ...state.bases.second }; newBases.second = null; }
      }
      if (state.bases.first) {
        const newBase = 1 + basesToAdvance;
        if (newBase >= 4) { runsScored++; scoringRunners.push(state.bases.first.name || 'Runner'); newBases.first = null; }
        else if (newBase === 3) { newBases.third = { ...state.bases.first }; newBases.first = null; }
        else if (newBase === 2) { newBases.second = { ...state.bases.first }; newBases.first = null; }
      }
      if (basesToAdvance === 4) { runsScored++; scoringRunners.push(currentBatter?.name || 'Batter'); }
      else if (basesToAdvance === 3) newBases.third = { ...batterData };
      else if (basesToAdvance === 2) newBases.second = { ...batterData };
      else if (basesToAdvance === 1) newBases.first = { ...batterData };
      if (scoringRunners.length > 0) showScoringToast(scoringRunners);
      if (runsScored > 0) {
        if (state.half === "bottom") newState.home_score += runsScored; else newState.away_score += runsScored;
        newState.inning_scores[state.inning - 1][state.half] = (newState.inning_scores[state.inning - 1][state.half] || 0) + runsScored;
      }
      newState.bases = newBases; newState.balls = 0; newState.strikes = 0;
      newState[getBatterIndexKey(state.half)] = nextBatterIndex(state, batters.length);
    }

    try {
      onUpdate(newState);
      if (halfInningEnded && onHalfInningEnd) onHalfInningEnd();
      // Always loop back to pitch after fielding. Steals are runner-tile interrupts now,
      // not a dedicated baserunning phase.
      setCurrentPhase("pitch");
    } catch { toast.error("Failed"); }
    setProcessing(false);
  };

  const clear = () => { setFieldRoll(""); setManagerMod(""); setResult(null); };

  // ── Online: select (pick cards, lock in) ─────────────────────────────────
  if (isOnline && !debugMode && onlinePhase === "select") {
    const accent = amIFielder
      ? { text: "text-emerald-400", border: "border-emerald-500", bg: "bg-emerald-500/20", btn: "bg-emerald-600 hover:bg-emerald-500 text-white" }
      : { text: "text-amber-400", border: "border-amber-500", bg: "bg-amber-500/20", btn: "bg-amber-500 hover:bg-amber-400 text-black" };
    const handleLockIn = () => {
      if (!wsSend) return;
      const cardTargets = {};
      if (toughHopSelected && effectiveToughHopTarget) {
        cardTargets.tough_hop = effectiveToughHopTarget.id;
      }
      if (directionalHittingSelected && effectiveDirectionalHittingTarget) {
        cardTargets.directional_hitting = effectiveDirectionalHittingTarget.id;
      }
      if (delayedReactionSelected && effectiveDelayedReactionTarget) {
        cardTargets.delayed_reaction = effectiveDelayedReactionTarget.id;
      }
      const sent = wsSend({ type: "fielding_lock_in", cards: myCards, card_targets: cardTargets });
      if (sent) setOnlinePhase("waiting");
      else toast.error("Connection lost — try again");
    };

    // Enter locks in, but only when the button is actually enabled.
    kbdFieldRef.current = (wsConnected && !toughHopNeedsTarget && !fielderCardNeedsTarget)
      ? handleLockIn : null;

    // Build the math expression: pitcher_roll [+chip] [+cards] = total  vs
    // batter_roll [+chip] [+cards] = total. Card names are appended below as
    // small badges if there's vertical room.
    const cardLookup = (id) => [...homeHand, ...awayHand].find(c => c.id === id) || allCards.find(c => c.id === id);
    const pCards = (pitchResult?.pitcher_cards || []).map(cardLookup).filter(Boolean);
    const bCards = (pitchResult?.batter_cards || []).map(cardLookup).filter(Boolean);
    const buildExpr = (dice, chipBonus, cardMod) => {
      if (dice == null) return null;
      const parts = [`${dice}`];
      if (chipBonus > 0) parts.push(`+${chipBonus}`);
      if (cardMod > 0) parts.push(`+${cardMod}`);
      else if (cardMod < 0) parts.push(`${cardMod}`);
      const total = dice + (chipBonus || 0) + (cardMod || 0);
      return { expr: parts.join(""), total, hasBonus: parts.length > 1 };
    };
    const pBonus = pitchResult?.chip_winner === "pitcher" ? (pitchResult.chip_bonus || 0) : 0;
    const bBonus = pitchResult?.chip_winner === "batter" ? (pitchResult.chip_bonus || 0) : 0;
    const pExpr = buildExpr(pitchResult?.pitcher_d10, pBonus, pitchResult?.pitcher_card_mod || 0);
    const bExpr = buildExpr(pitchResult?.batter_d10, bBonus, pitchResult?.batter_card_mod || 0);
    const haveExpr = pExpr && bExpr;

    return (
      <motion.div ref={fieldPanelContainerRef} data-testid="fielding-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-row items-stretch">
        {/* Left column: contact → power badge → math, resizable */}
        <div style={{ width: fieldPanelWidth }} className="flex flex-col px-4 py-3 gap-2 flex-shrink-0 overflow-hidden">
          {/* 1. Contact number — biggest element */}
          <div className="flex flex-col items-start gap-0.5">
            <span className="text-[9px] text-slate-600 uppercase tracking-widest font-bold">Contact</span>
            <span className={`text-4xl font-black leading-none tabular-nums ${power.color}`}>{contactValue}</span>
          </div>

          {/* 2. Power label badge */}
          <span className={`self-start text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-md border ${power.color} border-current bg-current/10`}>
            {power.label}
          </span>

          {/* 3. Math resolution — dice expression from pitch result */}
          {haveExpr ? (
            <div className="flex flex-col gap-1 mt-1">
              <div className="text-[11px] font-mono tabular-nums whitespace-nowrap">
                <span className="text-emerald-400 font-bold">{pExpr.expr}</span>
                <span className="text-slate-600 mx-1.5">vs</span>
                <span className="text-amber-400 font-bold">{bExpr.expr}</span>
              </div>
              {(pCards.length > 0 || bCards.length > 0) && (
                <div className="flex flex-wrap gap-1">
                  {pCards.map(c => (
                    <span key={`p-${c.id}`} className="text-[9px] px-1 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-emerald-400 font-bold whitespace-nowrap">
                      {c.name}
                    </span>
                  ))}
                  {bCards.map(c => (
                    <span key={`b-${c.id}`} className="text-[9px] px-1 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded text-amber-400 font-bold whitespace-nowrap">
                      {c.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <p className="text-[10px] text-slate-500 leading-snug mt-1">
              {amIFielder
                ? "Play a card to boost your D6, or lock in to take the play as-is."
                : "Play any card that affects fielding, or lock in."}
            </p>
          )}

          {/* Tough Hop status — prompts tile click or confirms selection */}
          {toughHopSelected && toughHopEligible.length > 1 && !toughHopTarget && (
            <div className="mt-1 px-2 py-1.5 rounded bg-indigo-500/10 border border-indigo-500/30 text-[9px] text-indigo-400 font-semibold">
              Click an infielder on the field ↗
            </div>
          )}
          {toughHopSelected && toughHopEligible.length > 1 && toughHopTarget && (
            <div className="mt-1 px-2 py-1.5 rounded bg-indigo-500/15 border border-indigo-500/40 text-[9px] text-indigo-300 font-bold flex items-center justify-between">
              <span>Tough Hop → {toughHopTarget.name}</span>
              <button onClick={() => setToughHopTarget(null)} className="text-[8px] text-indigo-500 hover:text-indigo-200 ml-2">✕</button>
            </div>
          )}
          {toughHopSelected && toughHopEligible.length === 1 && (
            <div className="mt-1 px-2 py-1.5 rounded bg-indigo-500/15 border border-indigo-500/30 text-[9px] text-indigo-300 font-bold">
              Tough Hop → {toughHopEligible[0].name} (auto)
            </div>
          )}

          {/* Directional Hitting / Delayed Reaction — diamond-based fielder target select */}
          {fielderCardSelected && fielderCardEligible.length > 1 && !effectiveFielderCardTarget && (
            <div className="mt-1 px-2 py-1.5 rounded bg-amber-500/10 border border-amber-500/30 text-[9px] text-amber-400 font-semibold">
              Click a highlighted fielder ↗
            </div>
          )}
          {fielderCardSelected && fielderCardEligible.length > 1 && effectiveFielderCardTarget && (
            <div className="mt-1 px-2 py-1.5 rounded bg-amber-500/15 border border-amber-500/40 text-[9px] text-amber-300 font-bold flex items-center justify-between">
              <span>
                {directionalHittingSelected && delayedReactionSelected ? "DH + DR" : directionalHittingSelected ? "Directional Hitting" : "Delayed Reaction"}
                {" → "}{effectiveFielderCardTarget.name}
              </span>
              <button onClick={() => setFielderCardTarget(null)} className="text-[8px] text-amber-500 hover:text-amber-200 ml-2">✕</button>
            </div>
          )}
          {fielderCardSelected && fielderCardEligible.length === 1 && (
            <div className="mt-1 px-2 py-1.5 rounded bg-amber-500/15 border border-amber-500/30 text-[9px] text-amber-300 font-bold">
              {directionalHittingSelected && delayedReactionSelected ? "DH + DR" : directionalHittingSelected ? "Directional Hitting" : "Delayed Reaction"}
              {" → "}{fielderCardEligible[0].name} (auto)
            </div>
          )}

          <div className="flex-1" />
          <button
            onClick={handleLockIn}
            data-testid="fielding-lock-in"
            data-blocked-reason={
              toughHopNeedsTarget ? "needs-tough-hop-target"
              : fielderCardNeedsTarget ? "needs-fielder-target"
              : !wsConnected ? "disconnected" : undefined
            }
            disabled={!wsConnected || toughHopNeedsTarget || fielderCardNeedsTarget}
            title={
              toughHopNeedsTarget ? "Select a Tough Hop target first"
              : fielderCardNeedsTarget ? "Click a highlighted fielder on the field first"
              : !wsConnected ? "Reconnecting…"
              : undefined
            }
            className={`w-full px-2 py-2 rounded-lg ${accent.btn} font-black text-[11px] transition-all disabled:opacity-40`}>
            {!wsConnected ? "Reconnecting…"
              : toughHopNeedsTarget ? "Pick Tough Hop target →"
              : fielderCardNeedsTarget ? "Pick a fielder ↗"
              : "Lock In →"}
          </button>
        </div>

        {/* Drag handle */}
        <div
          onMouseDown={(e) => { e.preventDefault(); setFieldPanelDragging(true); }}
          className="w-1 cursor-ew-resize hover:bg-amber-500/50 transition-colors flex-shrink-0 bg-slate-800"
        />

        {/* Right: card hand — takes all remaining space */}
        <div className="flex-1 min-w-0 min-h-0 py-2 pr-3">
          <ManagerCardHand
            cards={myHand}
            playedCardIds={myCards}
            spentCardIds={mySpent}
            activeCards={myActive}
            onDeactivate={onDeactivateCard}
            onToggle={(id) => {
              if (id === "tough_hop" && myCards.includes("tough_hop")) setToughHopTarget(null);
              if ((id === "directional_hitting" || id === "delayed_reaction") && fielderCardTarget) setFielderCardTarget(null);
              setMyCards(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
            }}
            onInstantPlay={(id, extra = {}) => {
              if (id === "hack_the_board" && onHackTheBoardPlay) { onHackTheBoardPlay(id); return; }
              if (!wsSend) return;
              const sent = wsSend({ type: "play_manager_card", card_id: id, ...extra });
              if (!sent) toast.error("Connection lost — try again");
              else if (onInstantCardPlayed) onInstantCardPlayed(id);
            }}
            maxPlays={myHand.length || 4}
            label="Your Hand"
            phase="fielding"
            teamRole={amIFielder ? "pitcher" : "batter"}
            gameState={augmentedGameState}
            currentBatter={currentBatter}
            currentPitcher={currentPitcher}
            oppActiveCards={(game?.[`${myRole === "home" ? "away" : "home"}_active_cards`] || []).filter(a => a && !a.burned)}
            lockedCardIds={Object.entries(game?.[`${myRole}_hand_locks`] || {}).filter(([, v]) => v > (game?.state?.inning || 1)).map(([k]) => k)}
            unlimitedStacking={!!game?.unlimited_stacking}
            activeListeners={activeListeners[myRole] || []}
            onToggleListener={onToggleListener ? (id) => onToggleListener(id, myRole) : null}
          />
        </div>
      </motion.div>
    );
  }

  // ── Online: waiting for opponent ─────────────────────────────────────────
  if (isOnline && !debugMode && onlinePhase === "waiting") {
    const opponentTeam = amIFielder ? battingTeam.name : fieldingTeam.name;
    const opponentLabel = amIFielder ? "batter is responding…" : "fielding team is reading the play…";
    return (
      <motion.div data-testid="fielding-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col items-center justify-center gap-2">
        <div className="w-5 h-5 border-2 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
        <p className="text-xs text-slate-500">
          <span className="font-bold text-slate-300">{opponentTeam}</span> {opponentLabel}
        </p>
        {actionTimer !== null && !game?.no_timers && (
          <p className={`text-[11px] tabular-nums font-mono font-bold ${actionTimer <= 10 ? "text-red-400 animate-pulse" : "text-slate-400"}`}>
            {actionTimer}s remaining
          </p>
        )}
        <p className="text-[10px] text-emerald-400 font-bold uppercase tracking-widest">✓ You're locked in</p>
      </motion.div>
    );
  }

  // ── Online: Error Card dramatic two-die reveal ───────────────────────────
  if (isOnline && !debugMode && onlinePhase === "resolution" && onlineFieldingResolution?.error_reroll_d6 != null) {
    const r = onlineFieldingResolution;
    const cardById = (id) => [...homeHand, ...awayHand].find(c => c.id === id) || allCards.find(c => c.id === id);
    const fieldingCards = (r.fielding_cards || []).map(id => cardById(id)).filter(Boolean);
    const _errFieldingBdNames = new Set((r.fielding_mod_breakdown || []).map(m => m.name));
    const _errFieldingPills = fieldingCards.filter(c => _errFieldingBdNames.has(c.name));
    const labeled = fieldingOutcomeLabel(r.outcome, state.outs);

    // Stage flags
    const firstLanded   = resolutionStage >= 3;
    const showRerollBanner = resolutionStage >= 4;
    const showSecondDie = resolutionStage >= 5;
    const secondLanded  = resolutionStage >= 6;
    const showComparison = resolutionStage >= 6;
    const showResult    = resolutionStage >= 7;

    const keptRoll   = r.field_d6; // min of the two
    const firstIsKept = r.original_field_d6 <= r.error_reroll_d6;

    return (
      <motion.div data-testid="fielding-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="h-full flex flex-col items-center justify-center gap-3 px-4 overflow-hidden">

        {/* Header */}
        <motion.p initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
          className="text-[11px] font-black text-red-400 uppercase tracking-widest">
          ⚡ Error Card
        </motion.p>

        {/* Contact badge */}
        <div className="flex items-center gap-2">
          <span className="text-[9px] text-slate-500 uppercase tracking-wider font-bold">Contact</span>
          <span className={`text-2xl font-black tabular-nums leading-none ${power.color}`}>{r.contact}</span>
          <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded border ${power.color} border-current bg-current/10`}>{power.label}</span>
        </div>

        {/* Two-die row */}
        <div className="flex items-center gap-4">

          {/* First die */}
          <div className="flex flex-col items-center gap-1">
            <span className="text-[9px] text-slate-400 uppercase tracking-wider font-bold">First Roll</span>
            <div className={`w-14 h-14 rounded-xl border-2 bg-slate-900/80 flex items-center justify-center font-mono font-black text-3xl transition-all
              ${!firstLanded ? "border-red-500/60 text-red-300 animate-pulse"
                : showComparison && !firstIsKept ? "border-slate-700/40 text-slate-500"
                : "border-red-500/70 text-red-200"}`}>
              {firstLanded ? r.original_field_d6 : diceDisplay}
            </div>
            {showComparison && !firstIsKept && (
              <span className="text-[10px] font-mono font-black text-slate-600 line-through">{r.original_field_d6}</span>
            )}
            {showComparison && firstIsKept && (
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
                className="text-[9px] font-black text-emerald-400/80 uppercase tracking-wider">kept</motion.span>
            )}
          </div>

          {/* Center separator */}
          <div className="flex flex-col items-center gap-0.5 w-10">
            {showRerollBanner ? (
              <motion.div initial={{ scale: 0.4, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
                transition={{ type: "spring", stiffness: 400, damping: 18 }}
                className="flex flex-col items-center gap-0.5">
                <span className="text-2xl leading-none">⚡</span>
                <span className="text-[8px] font-black text-red-400 uppercase tracking-wider">Reroll!</span>
              </motion.div>
            ) : (
              <span className="text-slate-700 text-lg">→</span>
            )}
          </div>

          {/* Second die */}
          {showSecondDie ? (
            <div className="flex flex-col items-center gap-1">
              <span className="text-[9px] text-slate-400 uppercase tracking-wider font-bold">Reroll</span>
              <div className={`w-14 h-14 rounded-xl border-2 bg-slate-900/80 flex items-center justify-center font-mono font-black text-3xl transition-all
                ${!secondLanded ? "border-red-400 text-red-200 animate-pulse"
                  : showComparison && firstIsKept ? "border-slate-700/40 text-slate-500"
                  : "border-red-500/70 text-red-200"}`}>
                {secondLanded ? r.error_reroll_d6 : diceDisplay2}
              </div>
              {showComparison && firstIsKept && (
                <span className="text-[10px] font-mono font-black text-slate-600 line-through">{r.error_reroll_d6}</span>
              )}
              {showComparison && !firstIsKept && (
                <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.3 }}
                  className="text-[9px] font-black text-emerald-400/80 uppercase tracking-wider">kept</motion.span>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <span className="text-[9px] text-slate-600 uppercase tracking-wider font-bold">Reroll</span>
              <div className="w-14 h-14 rounded-xl border-2 border-dashed border-slate-700/30 bg-slate-900/20 flex items-center justify-center font-mono font-black text-2xl text-slate-700">?</div>
            </div>
          )}
        </div>

        {/* "Takes the lower" reveal */}
        {showComparison && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
            className="flex flex-col items-center gap-0.5">
            <span className="text-[8px] text-red-400/60 uppercase tracking-wider font-bold">Takes the lower</span>
            <span className="text-3xl font-black text-red-200 tabular-nums leading-none">{keptRoll}</span>
            {r.fielding_mod !== 0 && (
              <span className="text-[9px] text-slate-500 font-mono">
                {keptRoll} {(r.fielding_mod_breakdown?.length > 0
                  ? r.fielding_mod_breakdown.map((m, i) => <span key={i}>{m.value > 0 ? "+" : ""}{m.value} {m.name}{i < r.fielding_mod_breakdown.length - 1 ? " " : ""}</span>)
                  : <span>{r.fielding_mod > 0 ? `+${r.fielding_mod}` : r.fielding_mod} cards</span>
                )} = <span className="text-emerald-400 font-bold">{r.field_total}</span>
              </span>
            )}
          </motion.div>
        )}

        {/* Final result */}
        {showResult && (
          <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.3 }}
            className="flex flex-col items-center gap-1.5">
            <div className="text-[11px] font-mono text-slate-500 tabular-nums">
              <span className="text-emerald-400 font-bold">{r.field_total}</span>
              <span className="mx-1.5 text-slate-600">vs</span>
              <span className={`font-bold ${power.color}`}>{r.contact}</span>
            </div>
            <div className={`${labeled.color} rounded-lg py-1.5 px-5 text-white text-center`}>
              <span className="text-sm font-black">{labeled.label}</span>
              {r.outcome === "hr" && (
                <span className="ml-2 text-xs font-bold opacity-90">{rollHrDistance(r.diff, r.resolution_id)} ft</span>
              )}
            </div>
          </motion.div>
        )}

        {/* Fielding cards */}
        {_errFieldingPills.length > 0 && (
          <div className="flex flex-wrap gap-1 justify-center">
            {_errFieldingPills.map(c => (
              <span key={c.id} className="text-[8px] px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-emerald-400 font-bold whitespace-nowrap">{c.name}</span>
            ))}
          </div>
        )}
      </motion.div>
    );
  }

  // ── Online: animated resolution (normal path) ────────────────────────────
  if (isOnline && !debugMode && onlinePhase === "resolution" && onlineFieldingResolution) {
    const r = onlineFieldingResolution;
    const cardById = (id) => [...homeHand, ...awayHand].find(c => c.id === id) || allCards.find(c => c.id === id);
    const fieldingCards = (r.fielding_cards || []).map(id => cardById(id)).filter(Boolean);
    const battingCards = (r.batting_cards || []).map(id => cardById(id)).filter(Boolean);
    // Only show pills for cards that contributed to the fielding outcome.
    const fieldingBdNames = new Set((r.fielding_mod_breakdown || []).map(m => m.name));
    const fieldingPills = fieldingCards.filter(c => fieldingBdNames.has(c.name));
    const battingPills = battingCards.filter(c => fieldingBdNames.has(c.name));
    const showCards = resolutionStage >= 1;
    const showDice = resolutionStage >= 2;
    const showResult = resolutionStage >= 4;
    const labeled = fieldingOutcomeLabel(r.outcome, state.outs);

    // Cap computation: capped mode stops the fielding die at raw roll + highest card max.
    // Unlimited mode lets it tick all the way to field_total.
    const _fieldUnlimited = !!game?.unlimited_stacking;
    const _allResCards = [...(r.fielding_cards || []), ...(r.batting_cards || [])].map(id => cardById(id)).filter(Boolean);
    const _fieldCardMaxes = _allResCards.map(c => c.max).filter(m => m != null && m > 0);
    const fieldDieCap = _fieldCardMaxes.length > 0 ? (r.field_d6 || 0) + Math.max(..._fieldCardMaxes) : 6;
    const fieldDieDisplay = (!_fieldUnlimited && fieldingDieAnimated != null)
      ? Math.min(fieldingDieAnimated, fieldDieCap)
      : (fieldingDieAnimated ?? diceDisplay);

    return (
      <motion.div data-testid="fielding-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }}
        className="h-full flex flex-col md:flex-row items-center justify-center gap-2 md:gap-6 px-3 overflow-hidden">

        {/* FIELDER COLUMN (left) — D6 + fielding cards */}
        {showCards && (
          <motion.div initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
            className="flex flex-col items-center gap-1 min-w-0 flex-shrink-0">
            <p className="text-[9px] text-emerald-400 font-bold uppercase tracking-widest">Fielder</p>
            <span className="text-lg font-black text-emerald-400 leading-none">D6</span>
            {showDice && (
              <div className="flex flex-col items-center gap-0.5">
                <div className={`w-12 h-12 rounded-lg border-2 bg-slate-900/80 flex items-center justify-center font-mono font-black text-2xl ${resolutionStage === 2 ? "animate-pulse border-emerald-500/60 text-emerald-400" : "border-emerald-500/60 text-emerald-400"}`}>
                  {fieldDieDisplay}
                </div>
                {/* Max cap label — visible once die lands; mods animate/stack below it */}
                {resolutionStage >= 3 && (
                  <span className="text-[8px] text-slate-500 font-mono tabular-nums">max {fieldDieCap}</span>
                )}
                {resolutionStage >= 3 && r.fielding_mod !== 0 && (
                  r.fielding_mod_breakdown?.length > 0
                    ? r.fielding_mod_breakdown.map((m, i) => (
                        <span key={i} className={`text-[9px] ${m.value > 0 ? "text-emerald-400/70" : "text-red-300/80"}`}>
                          {m.value > 0 ? "+" : ""}{m.value} {m.name}
                        </span>
                      ))
                    : <span className="text-[9px] text-emerald-400/70">{r.fielding_mod > 0 ? "+" : ""}{r.fielding_mod} cards</span>
                )}
              </div>
            )}
            {fieldingPills.length > 0 && (
              <div className="flex flex-wrap gap-1 justify-center max-w-[120px]">
                {fieldingPills.map(c => (
                  <span key={c.id} className="text-[8px] px-1.5 py-0.5 bg-emerald-500/10 border border-emerald-500/30 rounded text-emerald-400 font-bold whitespace-nowrap">{c.name}</span>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* CENTER — power label + totals + outcome */}
        {showCards && (
          <motion.div initial={{ opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3 }}
            className="flex flex-col items-center gap-1.5 min-w-0 flex-shrink-0">
            <div className={`px-2 py-1 rounded-lg text-[10px] font-black whitespace-nowrap bg-slate-800/60 ${power.color}`}>
              {power.label}
            </div>
            {showResult && (
              <>
                <div className="flex items-center gap-1.5 text-[11px] font-mono">
                  <span className="text-emerald-400 font-bold">{r.field_total != null ? r.field_total : "—"}</span>
                  <span className="text-slate-600">vs</span>
                  <span className="text-amber-400 font-bold">{r.contact != null ? r.contact : "—"}</span>
                </div>
                <motion.div initial={{ scale: 0.85, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 0.25 }}
                  className={`${labeled.color} rounded-lg py-1.5 px-4 text-white text-center whitespace-nowrap`}>
                  <span className="text-sm font-black">{labeled.label}</span>
                  {/* Fielded HR: append rolled distance — same seed (resolution_id)
                      as the cinematic counter so the badge and the floating ft
                      number agree. Power = diff (contact - field_total). */}
                  {r.outcome === "hr" && (
                    <span className="ml-2 text-xs font-bold opacity-90">
                      {rollHrDistance(r.diff, r.resolution_id)} ft
                    </span>
                  )}
                </motion.div>
                {r.through_the_legs_triggered && (
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                    className="px-2 py-1 rounded bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[8px] font-bold text-center whitespace-nowrap">
                    Through the Legs → Single
                  </motion.div>
                )}
                {r.play_at_plate_result && (
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                    className="flex flex-col items-center gap-0.5 px-2 py-1 rounded bg-sky-500/10 border border-sky-500/30 text-[8px] font-bold">
                    <span className="text-sky-400 uppercase tracking-wide">Play at the Plate</span>
                    <span className="font-mono text-slate-300">
                      {r.play_at_plate_fielder_d6} vs {r.play_at_plate_batter_d6}
                    </span>
                    <span className={r.play_at_plate_result === "fielder_wins" ? "text-rose-400" : r.play_at_plate_result === "rundown" ? "text-amber-400" : "text-emerald-400"}>
                      {r.play_at_plate_result === "fielder_wins" ? "Fielder Wins" : r.play_at_plate_result === "rundown" ? "Rundown!" : "Runner Scores"}
                    </span>
                  </motion.div>
                )}
                {r.block_the_plate_result && (
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                    className="flex flex-col items-center gap-0.5 px-2 py-1 rounded bg-orange-500/10 border border-orange-500/30 text-[8px] font-bold">
                    <span className="text-orange-400 uppercase tracking-wide">Block the Plate</span>
                    <span className="font-mono text-slate-300">
                      {r.block_the_plate_runner_d6} vs {r.block_the_plate_catcher_d6}
                    </span>
                    <span className={r.block_the_plate_result === "catcher_wins" ? "text-rose-400" : "text-emerald-400"}>
                      {r.block_the_plate_result === "catcher_wins" ? "Runner Out!" : "Runner Scores"}
                    </span>
                  </motion.div>
                )}
                {r.tough_hop_triggered && (
                  <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
                    className="px-2 py-1 rounded bg-indigo-500/15 border border-indigo-500/30 text-indigo-300 text-[8px] font-bold text-center whitespace-nowrap">
                    Tough Hop → {r.tough_hop_target_name || "target"} −2
                  </motion.div>
                )}
              </>
            )}
          </motion.div>
        )}

        {/* BATTER COLUMN (right) — contact value + batter cards */}
        {showCards && (
          <motion.div initial={{ opacity: 0, x: 6 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.3 }}
            className="flex flex-col items-center gap-1 min-w-0 flex-shrink-0">
            <p className="text-[9px] text-amber-400 font-bold uppercase tracking-widest">Batter</p>
            <span className="text-lg font-black text-amber-400 leading-none">Contact</span>
            {showDice && (
              <div className="w-12 h-12 rounded-lg border-2 border-amber-500/60 bg-slate-900/80 flex items-center justify-center font-mono font-black text-2xl text-amber-400">
                {r.contact != null ? r.contact : "—"}
              </div>
            )}
            {battingPills.length > 0 && (
              <div className="flex flex-wrap gap-1 justify-center max-w-[120px]">
                {battingPills.map(c => (
                  <span key={c.id} className="text-[8px] px-1.5 py-0.5 bg-amber-500/10 border border-amber-500/30 rounded text-amber-400 font-bold whitespace-nowrap">{c.name}</span>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </motion.div>
    );
  }

  // Single-screen flow: inline layout with badge at end of line
  return (
    <motion.div data-testid="fielding-phase" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="h-full flex flex-col items-center justify-center gap-3">
      {/* Main horizontal row: Contact | vs | D6 + Mgr | = Result | Badge */}
      <div className="flex items-center flex-wrap gap-2 md:gap-4 justify-center">
        {/* Editable Contact + Power label */}
        <span className={`text-sm font-bold ${power.color}`}>{power.label}</span>
        <div className="flex items-center gap-2">
          <input
            type="text" inputMode="numeric" value={contactValue}
            onChange={(e) => { 
              const v = e.target.value; 
              if (v === "" || /^\d+$/.test(v)) setContactValue(v === "" ? 0 : parseInt(v)); 
            }}
            className="w-10 h-10 text-2xl text-center font-mono font-black bg-transparent border-b-2 border-emerald-500/50 text-emerald-400 outline-none focus:border-emerald-400 transition-all"
          />
          {/* <span className="text-[10px] text-slate-500">contact</span> */}
        </div>

        {/* Separator */}
        <span className="text-slate-600 font-bold">vs</span>

        {/* D6 + Mgr */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-slate-500 font-bold">D6</span>
            <motion.input
              data-testid="field-roll-input"
              type="text" inputMode="numeric" value={fieldRoll}
              onChange={(e) => { const v = e.target.value; if (v === "" || (/^\d$/.test(v) && parseInt(v) >= 1 && parseInt(v) <= 6)) setFieldRoll(v); }}
              placeholder="—"
              animate={isRolling ? { scale: [1, 1.1, 1] } : {}}
              transition={{ duration: 0.1, repeat: isRolling ? Infinity : 0 }}
              className={`w-12 h-10 text-center text-xl font-mono font-black bg-slate-800/80 border-2 rounded-lg outline-none transition-all ${
                isRolling ? 'border-amber-500 text-amber-400' : 'border-emerald-500/50 text-emerald-400 focus:ring-2 focus:ring-emerald-500/40'
              }`}
            />
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-slate-500 font-bold">Mgr</span>
            <input
              type="text" inputMode="numeric" value={managerMod}
              onChange={(e) => { const v = e.target.value; if (v === "" || v === "-" || /^-?\d$/.test(v)) setManagerMod(v); }}
              placeholder="0"
              className="w-8 h-7 text-center text-xs font-mono font-bold bg-slate-800/50 border border-slate-700/50 rounded-md text-slate-300 outline-none focus:ring-1 focus:ring-slate-500/30 transition-all"
            />
          </div>
          {fieldingCardBonus > 0 && (
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-slate-500 font-bold">Cards</span>
              <span className="text-[10px] font-bold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-1.5 py-0.5 rounded">+{fieldingCardBonus}</span>
            </div>
          )}
        </div>

        {/* Animated "= result" then badge at end of line */}
        {result && (
          <>
            <motion.div 
              initial={{ opacity: 0, x: -10 }} 
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
              className="flex items-center gap-2"
            >
              <span className="text-slate-600 font-bold">=</span>
              <span className="text-xl font-black text-white font-mono">{result.diff}</span>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }} 
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.25, duration: 0.3 }}
              className={`${result.color} rounded-lg py-1 px-3 text-white`}
            >
              <span className="text-xs font-black">{result.label}</span>
            </motion.div>
          </>
        )}
      </div>

      {/* Buttons */}
      <div className="flex items-center gap-2">
        {!result ? (
          <motion.button 
            data-testid="roll-d6-btn" 
            onClick={rollD6}
            disabled={isRolling || (!isOnline && !!isGameOver(state, game.innings_total || 9))}
            animate={isRolling ? { scale: [1, 1.05, 1] } : {}}
            transition={{ duration: 0.2, repeat: isRolling ? Infinity : 0 }}
            className={`px-5 py-2 rounded-lg text-white font-bold text-sm flex items-center gap-2 transition-all active:scale-95 shadow-md ${
              isRolling ? 'bg-amber-500 shadow-amber-500/20' : 'bg-emerald-600 hover:bg-emerald-500 shadow-emerald-500/20'
            }`}
          >
            <motion.div animate={isRolling ? { rotate: 360 } : {}} transition={{ duration: 0.3, repeat: isRolling ? Infinity : 0 }}>
              <Dices size={14} />
            </motion.div>
            {isRolling ? "Rolling..." : "Roll D6"}
          </motion.button>
        ) : (
          <motion.div 
            initial={{ y: 10, opacity: 0 }} 
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.35 }}
            className="flex items-center gap-2"
          >
            <button 
              onClick={clear} 
              className="px-3 py-1 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold text-xs transition-all"
            >
              Re-roll
            </button>
            <button 
              onClick={applyResult}
              disabled={processing || (!isOnline && !!isGameOver(state, game.innings_total || 9))}
              className="px-4 py-1 rounded-md bg-amber-500 hover:bg-amber-400 text-black font-bold text-xs transition-all"
            >
              {processing ? "..." : "Apply"}
            </button>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}
